import { Button } from 'antd';
import React, { useCallback } from 'react';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  supportedMetricsSelector,
  getFirstAppliedFilter,
  intervalSelector,
} from '@store/common/selectors';
import {
  allDimensionsListSelector,
  dimensionsListSelectorKeyValue,
} from '@store/common/dimensionList/selectors';
import {
  getAppEventsPreset,
  getServerSidePerformancePreset,
} from './Dashboard/UseCases/configs/app';
import { getValidateDimensionFunc, getValidateMetricFunc } from './Dashboard/UseCases/utils';
import { drilldownDashboardType } from './Dashboard/UseCases';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { isAppModule } from '@utils/constants';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { selector as summaryContainerSelector } from '@store/summaryContainer';
import { createFilterRule } from '@utils/filter';
import AnalysisIcon from '@icons/analysis.svg';
import { initialFilter } from '@src/store/common/reducer';

const appEvents = ['event_name', 'tag_key', 'tag_value'];
const serverSidePerformance = [
  'network_request_url_host',
  'network_request_url_port',
  'network_request_url_path',
  'network_request_http_method',
  'network_request_response_code',
  'network_request_url_scheme',
  'network_request_url_query',
];
const dimensionDrillDownTarget = Object.entries({
  appEvents,
  serverSidePerformance,
}).reduce<object>((res, [k, v]) => {
  return {
    ...res,
    ...v.reduce<object>((sum, it) => {
      return { ...sum, [it]: k };
    }, {}),
  };
}, {});
const getPresetConfigFunc = (target: 'appEvents' | 'serverSidePerformance') => {
  return {
    appEvents: getAppEventsPreset,
    serverSidePerformance: getServerSidePerformancePreset,
  }[target];
};
const getDrillDownIcon = (): JSX.Element => {
  return <AnalysisIcon fill="white" />;
};

/**
 * Drill down to perset Event Scope, take along with current global filter and dimension filter with current value.
 */
export const DrillDownToEventScope: React.FC<{ dimension: string; rowData: object }> = ({
  dimension,
  rowData,
}) => {
  const metrics = useSelector(supportedMetricsSelector) as object;
  const dimensions = useSelector(dimensionsListSelectorKeyValue) as object;
  const dimensionList = useSelector(allDimensionsListSelector);
  const globalFilter = useSelector(getFirstAppliedFilter);
  const formattedInterval = useSelector(intervalSelector);
  const summaryContainer = useSelector(summaryContainerSelector);
  const dispatch = useDispatch();
  const dimensionInfo = dimensionList.find((it) => it.groupBy === dimension);
  const targetPreset = dimensionDrillDownTarget[dimension];

  const handleActions = useCallback(() => {
    const { name: dimensionName } = dimensionInfo;
    const filterByValue = createFilterRule(
      dimensionList,
      dimensionName,
      get(rowData, 'name[0]', ''),
      get(rowData, 'value[0]', ''),
    );

    /** The logic of handling drilldown and globalFilter is referred from
     *  function dimensionDrillown to ensure the structure is consistent. */
    const crumbRules = getRulesFromFilter([[filterByValue]], true);
    const crumbFilter = getFilterFromRules(crumbRules, true);
    const drilldownFilters = [initialFilter, crumbFilter];
    const combinedFilter = [[filterByValue]];
    const newRules = getRulesFromFilter(combinedFilter, true);
    const newFilter = getFilterFromRules(newRules, true);

    const getPresetConfig = getPresetConfigFunc(targetPreset);
    if (!getPresetConfig) {
      return;
    }
    const { config, title, key } = getPresetConfig({
      validateDimension: getValidateDimensionFunc(dimensions),
      validateMetric: getValidateMetricFunc(metrics),
    });
    dispatch(
      createTempDashboard(
        drilldownDashboardType,
        {
          ...config,
          common: {
            appliedFilters: [newFilter],
            interval: formattedInterval,
          },
          summary: {
            drilldownData: drilldownFilters,
          },
          usecaseKey: key,
        },
        title,
      ),
    );
  }, [
    dimensionInfo,
    dimensionList,
    rowData,
    targetPreset,
    dimensions,
    metrics,
    dispatch,
    formattedInterval,
  ]);

  if (!isAppModule || !dimensionInfo || dimensionDrillDownTarget[dimension] === undefined) {
    return null;
  }
  return (
    <Button
      type="text"
      style={{
        backgroundColor: '#0046ff',
        color: 'white',
        width: '40px',
      }}
      icon={getDrillDownIcon()}
      onClick={handleActions}
    />
  );
};
